/*
 * NodeMenu: which box (or phone) the console is showing, and the way to show
 * another.
 *
 * It is the first thing in the sidebar, under the logo, as a card naming the
 * box and whether it answers. At the sidebar's foot, under thirty
 * destinations, people looked past it and read one box's settings as
 * another's; a pill at the top right was still easy to miss. On a phone the
 * sidebar folds away behind the menu button, so the same picker sits in the
 * header bar there (`placement="header"`).
 *
 * The dot says what the console knows: green while the selected box answers,
 * amber once it stops (as Fleet Overview marks an offline box), grey before
 * the first answer. Nothing renders on the on-device console, which shows one
 * box only.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCheck, FiChevronDown, FiSearch, FiServer, FiSmartphone } from "react-icons/fi";
import { ago } from "../utils/formatters";
import { isMobileNode } from "../utils/nodeKind";

// Past this many, the list gets a filter: a chain's twenty-five shops are
// found by typing a name, not by scrolling for it.
export const FILTER_FROM = 8;

const REACH = {
  ok: { dot: "ok", word: "Connected" },
  down: { dot: "off", word: "Not answering" },
  unknown: { dot: "wait", word: "Checking" },
};

function subline(node) {
  const parts = [];
  if (isMobileNode(node)) parts.push("Phone");
  if (node.stale) parts.push(node.last_seen ? `offline, last seen ${ago(node.last_seen)}` : "offline");
  const s = parts.join(", ");
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}

export default function NodeMenu({ nodes = [], selectedNode, onSelect, health, singleEdge = false, placement = "sidebar" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const filterRef = useRef(null);

  // A click anywhere else, or Escape, closes the list.
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    if (filterRef.current) filterRef.current.focus();
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const shown = useMemo(
    () => (q ? nodes.filter((n) => (n.node_id || "").toLowerCase().includes(q)) : nodes),
    [nodes, q],
  );

  if (singleEdge || nodes.length === 0) return null;

  const reach = !selectedNode ? null : REACH[health?.status] || REACH.unknown;
  const current = nodes.find((n) => n.node_id === selectedNode);
  const phone = isMobileNode(current);
  const KindIcon = phone ? FiSmartphone : FiServer;

  const pick = (id) => {
    setOpen(false);
    setQuery("");
    if (id !== selectedNode) onSelect(id);
  };

  return (
    <div className={`node-menu node-menu--${placement}`} ref={ref}>
      <button
        type="button"
        className="node-menu-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={selectedNode ? `Showing ${selectedNode}, ${reach.word.toLowerCase()}. Choose another box` : "Choose a box"}
        title="Choose which box this console shows"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="node-menu-icon" aria-hidden="true">
          <KindIcon size={17} />
          {reach && <span className={`node-menu-dot ${reach.dot}`} />}
        </span>
        <span className="node-menu-text" aria-hidden="true">
          <span className="node-menu-caption">{phone ? "Phone" : "Box"}</span>
          <span className="node-menu-name">{selectedNode || "Choose a box"}</span>
          {reach && <span className={`node-menu-reach ${reach.dot}`}>{reach.word}</span>}
        </span>
        <FiChevronDown size={16} aria-hidden="true" className="node-menu-caret" />
      </button>
      {open && (
        <div className="pop-menu-list" role="group" aria-label="Boxes">
          {nodes.length > FILTER_FROM && (
            <label className="node-menu-filter">
              <FiSearch size={13} aria-hidden="true" />
              <input
                ref={filterRef}
                type="search"
                placeholder="Find a box"
                aria-label="Find a box"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && shown.length > 0) pick(shown[0].node_id);
                }}
              />
            </label>
          )}
          <div className="node-menu-items">
            {shown.length === 0 && <div className="node-menu-empty">No box matches “{query.trim()}”.</div>}
            {shown.map((n) => {
              const here = n.node_id === selectedNode;
              const sub = subline(n);
              return (
                <button
                  key={n.node_id}
                  type="button"
                  className={`pop-menu-item${here ? " current" : ""}`}
                  aria-current={here ? "true" : undefined}
                  onClick={() => pick(n.node_id)}
                >
                  <span className="pop-menu-name">
                    <span className={`node-menu-dot ${n.stale ? "off" : "ok"}`} aria-hidden="true" />
                    {n.node_id}
                  </span>
                  {sub && <span className="pop-menu-sub">{sub}</span>}
                  {here && <FiCheck size={15} aria-hidden="true" className="pop-menu-check" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
